import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Color, MeshStandardMaterial } from "three";

import { Clone, useCursor } from "@react-three/drei";

import { ref } from "valtio";
import { cameraStore } from "../../appStore";

import Hotspot from "../ui/Hotspot";

import SelectionMarker from "./SelectionMarker";
import SubSection from "./SubSection";
import useAnalyticsEventTracker from "../../hooks/useAnalyticsEventTracker";
import { Ebbne, MountWithEbbne, useEbbne } from "@solidwhite/ebbnen";
import { UIPortal } from "@solidwhite/ui-portal";
import SideBar from "../ui/SideBar/SideBar";
import { useSynchronizedState } from "@solidwhite/multiuser";

const findObjectStartsWithKey = ({ key, object }) => {
  if (object == null) {
    return null;
  }

  var obj = null;
  object.traverse((child) => {
    if (child.name.startsWith(key)) {
      obj = child;
    }
  });
  return obj;
};

const findChildStartsWithKey = ({ key, object }) => {
  if (object == null) {
    return null;
  }

  var obj = object.children.find((child) => child.name.startsWith(key));
  return obj;
};

const Section = ({ object3d, id, ...props }) => {
  const { t } = useTranslation();

  const { active, visible, select, selectable, ebbneId } = useEbbne();

  const gaEventTracker = useAnalyticsEventTracker("Hotspots");

  const [hovered, setHovered] = useSynchronizedState(`sectionhover_${ebbneId}`, false);

  useCursor(hovered);

  const object = useMemo(() => {
    return object3d.clone();
  }, []);

  const selectioncolliderObject = useMemo(() => {
    if (!object) return null;
    var obj = findObjectStartsWithKey({
      key: "collider",
      object: object,
    });

    obj.layers.set(2);

    return obj;
  }, [object]);

  const model = useMemo(() => {
    if (!object) return null;
    return findObjectStartsWithKey({
      key: "model",
      object: object,
    });
  }, [object]);

  const hotspot = useMemo(() => {
    if (!object) return null;
    return findObjectStartsWithKey({
      key: "label",
      object: object,
    });
  }, [object]);

  const cameraTargetObject = useMemo(() => {
    if (!object) return null;
    return object.children.find((c) => c.name.startsWith("CameraTarget"));
  }, [object]);

  const factsnFiguresMaterial2 = useMemo(() => {
    var mat = new MeshStandardMaterial();
    mat.color = new Color("#8ab19c");
    mat.metalness = 0.9;
    mat.roughness = 0.4;
    mat.envMapIntensity = 0.2;

    return mat;
  }, []);

  useEffect(() => {
    if (model) {
      model.traverse((c) => {
        if (c.type == "Mesh") {
          if (c.material.name == "MA_Kachel") {
            c.material = factsnFiguresMaterial2;
          }
        }
      });
    }
  }, [model]);

  const handleClick = (e) => {
    e.stopPropagation();
    select();
    gaEventTracker(`${id} click`);
  };

  useEffect(() => {
    if (active) {
      cameraStore.cameraTargetSettings = ref(cameraTargetObject);
    }
  }, [active]);

  useEffect(() => {
    if (!selectable) {
      setHovered(false);
    }
  }, [selectable]);

  return (
    <group visible={true}>
      <group>
        {model && (
          <group visible={visible}>
            <primitive object={model} />
          </group>
        )}

        {selectioncolliderObject && visible && (
          <SelectionMarker
            position={[selectioncolliderObject.position.x, 0, selectioncolliderObject.position.z]}
            selected={hovered}
            visible={selectable}
            lineMargin={0.4}
          >
            <group
              visible={false}
              onClick={selectable ? handleClick : () => {}}
              onPointerOver={() => {
                if (selectable) {
                  setHovered(true);
                }
              }}
              onPointerOut={() => setHovered(false)}
            >
              <Clone
                object={selectioncolliderObject}
                layers={[1]}
                position={[0, selectioncolliderObject.position.y, 0]}
                ref={(r) => {
                  if (r) r.children.forEach((c) => c.layers.set(1));
                }}
              />
            </group>
          </SelectionMarker>
        )}

        {hotspot && visible && selectable && (
          <group>
            <Hotspot
              position={hotspot.position}
              text={hotspot.userData.textId ? t(hotspot.userData.textId) : t(`L1_${id.toUpperCase()}_NAME`)}
              topLeft
              onClick={selectable ? handleClick : () => {}}
              onPointerOver={() => {
                if (selectable) {
                  setHovered(true);
                }
              }}
              onPointerOut={() => setHovered(false)}
            />
          </group>
        )}
      </group>

      <MountWithEbbne>
        <UIPortal>
          <SideBar key={`Sidebar_${ebbneId}`} contentId={ebbneId} />
        </UIPortal>
      </MountWithEbbne>
    </group>
  );
};

export default Section;
